import { Warning, HeartStraight } from "@phosphor-icons/react";
import { AIS_REGIONS, issBucket } from "@/lib/iss";

const TONE = {
  success: "bg-[#E8EFEA] text-[#4A5D4E] border-[#4A5D4E]/25",
  warning: "bg-[#F9EDE6] text-[#8C3A27] border-[#8C3A27]/25",
  danger: "bg-[#8C3A27] text-white border-[#8C3A27]",
  muted: "bg-[#EBE7DF] text-[#5C665A] border-[#5C665A]/20",
};

export default function IssSummary({ stats }) {
  const { iss, unsurvivable, regions, top3 } = stats;
  const bucket = issBucket(iss);

  return (
    <section
      className="bg-white border border-[#E5E0D8] rounded-md px-4 py-3 shadow-sm flex items-center gap-5 flex-wrap"
      data-testid="iss-summary"
    >
      <div className="flex items-center gap-3 pr-5 border-r border-[#E5E0D8]">
        <div className="w-10 h-10 rounded-md bg-[#F4F1EB] border border-[#E5E0D8] flex items-center justify-center">
          <HeartStraight size={20} weight="duotone" className="text-[#8C3A27]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665A] leading-none">
            Injury Severity Score
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span
              className="font-serif text-3xl font-semibold text-[#2C332A] leading-none"
              data-testid="iss-value"
            >
              {iss}
            </span>
            <span className="text-xs text-[#5C665A] font-mono">/ 75</span>
            <span
              className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded border ${TONE[bucket.tone]}`}
              data-testid="iss-bucket"
            >
              {bucket.label}
            </span>
          </div>
          <p className="text-[10px] text-[#5C665A] mt-1.5 leading-snug">
            Σ of squares of top-3 region AIS · trauma only · excludes medical conditions
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
        {AIS_REGIONS.map((r) => {
          const ais = regions[r] || 0;
          const used = top3.some((t) => t.region === r);
          return (
            <div
              key={r}
              className={`flex items-center gap-1.5 rounded border px-2 py-1 text-[11px] ${
                used ? "border-[#8C3A27]/40 bg-[#F9EDE6]/60" : "border-[#E5E0D8] bg-[#F4F1EB]/60"
              }`}
              data-testid={`ais-region-${r.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
            >
              <span className="text-[10px] uppercase tracking-wider text-[#5C665A]">{r}</span>
              <span className="font-mono font-semibold text-[#2C332A]">
                {ais > 0 ? ais : "—"}
              </span>
            </div>
          );
        })}
      </div>

      {unsurvivable && (
        <div className="flex items-center gap-2 text-[11px] text-[#8C3A27]">
          <Warning size={14} weight="fill" />
          AIS 6 present — ISS automatically 75
        </div>
      )}
    </section>
  );
}
